import { inventoryItemIdFormat } from "@redotech/redo-inventory-id";
import { processingSessionIdFormat } from "@redotech/redo-return-id";
import { returnsProcessingSessionType } from "@redotech/redo-return-warehouse-processing-db/processing-types";
import * as z from "zod";

export const ItemReviewLookupResultKind = {
  notFound: "not_found",
  notProcessed: "not_processed",
  ready: "ready",
} as const;
export type ItemReviewLookupResultKind =
  (typeof ItemReviewLookupResultKind)[keyof typeof ItemReviewLookupResultKind];

export const ReviewAnswerOutcome = { pass: "pass", fail: "fail" } as const;
export type ReviewAnswerOutcome =
  (typeof ReviewAnswerOutcome)[keyof typeof ReviewAnswerOutcome];

const ReviewItemCard_ = z.object({
  inventoryItemId: inventoryItemIdFormat.schema,
  inventoryItemNumber: z.string(),
  vendor: z.string().nullable(),
  productTitle: z.string().nullable(),
  variantTitle: z.string().nullable(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  imageUrl: z.string().nullable(),
  orderName: z.string().nullable(),
  rmaReference: z.string().nullable(),
  grade: z.string().nullable(),
});
export const ReviewItemCard: z.ZodType<ReviewItemCard> = ReviewItemCard_;
export interface ReviewItemCard extends z.infer<typeof ReviewItemCard_> {}

const ReviewAnswer_ = z.object({
  prompt: z.string(),
  outcome: z
    .enum([ReviewAnswerOutcome.pass, ReviewAnswerOutcome.fail])
    .nullable(),
  decision: z.boolean().nullable(),
  choiceLabel: z.string().nullable(),
  notes: z.string().nullable(),
  photos: z.array(z.string()),
});
export const ReviewAnswer: z.ZodType<ReviewAnswer> = ReviewAnswer_;
export interface ReviewAnswer extends z.infer<typeof ReviewAnswer_> {}

const ItemReview_ = z.object({
  sessionId: processingSessionIdFormat.schema,
  sessionType: z.enum(returnsProcessingSessionType).nullable(),
  completedAt: z.date(),
  processedByName: z.string().nullable(),
  answers: z.array(ReviewAnswer_),
  grade: z.string().nullable(),
});
export const ItemReview: z.ZodType<ItemReview> = ItemReview_;
export interface ItemReview extends z.infer<typeof ItemReview_> {}

const ItemReviewLookupResult_ = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal(ItemReviewLookupResultKind.notFound) }),
  z.object({
    kind: z.literal(ItemReviewLookupResultKind.notProcessed),
    item: ReviewItemCard_,
  }),
  z.object({
    kind: z.literal(ItemReviewLookupResultKind.ready),
    item: ReviewItemCard_,
    review: ItemReview_,
  }),
]);
export type ItemReviewLookupResult = z.infer<typeof ItemReviewLookupResult_>;

export const getItemForReview = {
  name: "getItemForReview" as const,
  input: z.object({ identifier: z.string().min(1) }),
  output: z.object({ result: ItemReviewLookupResult_ }),
};
